/**
 * `useSearchResults` — assemble the palette's four groups from one query.
 *
 * Extracted from the old modal component when the palette moved in-place, so
 * the trigger can own input + keyboard state without also carrying result
 * assembly. Pure derivation: no DOM, no open/closed concept, no side effects.
 *
 * Group order is fixed and deliberate — Pages, Candidates, Jobs, Help — so the
 * flat index the arrow keys walk is stable while a slower server group settles
 * in underneath. Reordering by relevance would move rows under the user's
 * cursor mid-keystroke.
 *
 * The client groups (Pages, Help) derive from `query` directly so they react on
 * the first character; the server groups come from `serverData`, which the
 * caller feeds from a debounced request. That split is why the palette feels
 * instant while still being cheap on the network.
 */
import { useMemo } from "react";
import { SEARCH_GROUP_LIMIT, type GlobalSearchResults } from "@/hooks/queries/search/useGlobalSearch";
import { matchNavDestinations, type ResolvedNavDestination } from "@/components/v2/search/navDestinations";
import { searchHelpDocs } from "@/components/v2/search/helpSearch";

export interface PaletteRow {
  /** Unique within the palette; the option's DOM id is derived from it. */
  id: string;
  title: string;
  /** Muted second line — section, email, or help snippet. */
  subtitle?: string;
  /** Trailing chip (JD status). */
  meta?: string;
  href: string;
}

export interface PaletteGroup {
  label: string;
  rows: PaletteRow[];
}

export function useSearchResults(
  query: string,
  destinations: readonly ResolvedNavDestination[],
  serverData: GlobalSearchResults | undefined,
  base: string,
): { groups: PaletteGroup[]; flatRows: PaletteRow[] } {
  return useMemo(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) return { groups: [], flatRows: [] };

    const groups: PaletteGroup[] = [];

    const pages = matchNavDestinations(destinations, trimmed).slice(0, SEARCH_GROUP_LIMIT);
    if (pages.length > 0) {
      groups.push({
        label: "Pages",
        rows: pages.map((d) => ({
          id: d.id,
          title: d.label,
          subtitle: d.section,
          href: d.href,
        })),
      });
    }

    const candidates = serverData?.candidates ?? [];
    if (candidates.length > 0) {
      groups.push({
        label: "Candidates",
        rows: candidates.map((c) => ({
          id: `cand-${c.id}`,
          title: c.name,
          subtitle: c.email ?? undefined,
          href: `${base}/candidates/${c.id}`,
        })),
      });
    }

    const jds = serverData?.jds ?? [];
    if (jds.length > 0) {
      groups.push({
        label: "Jobs",
        rows: jds.map((j) => ({
          id: `jd-${j.id}`,
          title: j.name,
          meta: j.status,
          href: `${base}/jobs/${j.id}`,
        })),
      });
    }

    const help = searchHelpDocs(trimmed, base, SEARCH_GROUP_LIMIT);
    if (help.length > 0) {
      groups.push({
        label: "Help Center",
        rows: help.map((h) => ({
          id: h.id,
          title: h.title,
          subtitle: h.snippet.length > 0 ? h.snippet : undefined,
          href: h.href,
        })),
      });
    }

    return { groups, flatRows: groups.flatMap((g) => g.rows) };
  }, [query, destinations, serverData, base]);
}
